import { SUPPORT_EMAIL } from "@/components/PublicLayout";

const LAST_UPDATED = "February 11, 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="mb-10 scroll-mt-24" data-testid={`privacy-section-${id}`}>
    <h2 className="font-display text-xl sm:text-2xl mb-3 text-white">{title}</h2>
    <div className="text-white/70 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="text-xs uppercase tracking-wider text-cyan-300/80 mb-3">
        Legal
      </div>
      <h1
        className="font-display text-3xl sm:text-4xl tracking-tight mb-3"
        data-testid="privacy-heading"
      >
        Privacy Policy
      </h1>
      <p className="text-white/45 text-sm mb-10">Last updated: {LAST_UPDATED}</p>

      <Section id="overview" title="Overview">
        <p>
          Journey is a private, single-user AI assistant operated by the account owner.
          This policy describes what data Journey processes, how it's stored, who has
          access, and the rights available to anyone whose information may be involved
          (including SMS recipients of delegated follow-ups).
        </p>
        <p>
          Questions, opt-out requests, and data-removal requests can be sent to{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <Section id="data-we-collect" title="Information we collect">
        <p>Journey collects the minimum data needed to operate the assistant:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong className="text-white/85">Account-owner content</strong> — chat
            messages, reminders, memories, uploaded files, and settings the owner
            creates inside the app.
          </li>
          <li>
            <strong className="text-white/85">Contact records</strong> — when the owner
            creates a delegated task, they may enter a contact's name and phone number.
          </li>
          <li>
            <strong className="text-white/85">SMS message data</strong> — for delegated
            tasks, the body of inbound and outbound text messages, the phone numbers
            involved, and Twilio's message metadata (timestamp, message id, delivery
            status).
          </li>
          <li>
            <strong className="text-white/85">Technical data</strong> — basic logs needed
            to keep the service running (errors, request timing). No third-party
            analytics or ad trackers are used.
          </li>
        </ul>
      </Section>

      <Section id="sms" title="SMS messaging">
        <p>
          Journey uses Twilio to send and receive SMS only on the account owner's
          behalf, only for delegated tasks the owner explicitly initiates. Messaging is
          transactional and operational — there is no marketing, no promotional, and no
          bulk messaging.
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong className="text-white/85">Consent</strong> — phone numbers are
            entered manually by the account owner, who is responsible for having a
            prior relationship and permission to message that contact.
          </li>
          <li>
            <strong className="text-white/85">Identification</strong> — outbound
            messages include context about who initiated the follow-up.
          </li>
          <li>
            <strong className="text-white/85">Opt-out</strong> — any recipient can
            reply <code className="text-cyan-200">STOP</code>,{" "}
            <code className="text-cyan-200">UNSUBSCRIBE</code>, or{" "}
            <code className="text-cyan-200">END</code> to stop receiving messages.
            Recipients can also email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
              {SUPPORT_EMAIL}
            </a>{" "}
            to request opt-out and deletion of any associated records.
          </li>
          <li>
            <strong className="text-white/85">Help</strong> — replying{" "}
            <code className="text-cyan-200">HELP</code> returns this contact email.
          </li>
          <li>
            <strong className="text-white/85">Carrier rates</strong> — standard
            message-and-data rates may apply to recipients.
          </li>
        </ul>
      </Section>

      <Section id="how-we-use" title="How we use information">
        <p>Journey uses collected data exclusively to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Operate the assistant (respond to the owner, manage reminders, save memories).</li>
          <li>Send and receive SMS for delegated tasks the owner created.</li>
          <li>Maintain reliability, fix bugs, and respond to support requests.</li>
        </ul>
        <p>
          Journey does <strong>not</strong> sell data, share it with advertisers, or use
          it to train third-party AI models. Personal data is never shared for marketing
          purposes.
        </p>
      </Section>

      <Section id="third-parties" title="Third-party services">
        <p>
          To provide the service, Journey relies on a small set of vetted infrastructure
          providers, each receiving only the data necessary for their function:
        </p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            <strong className="text-white/85">OpenAI / Anthropic / Google</strong> —
            large language models, accessed through a managed proxy, used to power
            chat responses, memory extraction, and visual generation.
          </li>
          <li>
            <strong className="text-white/85">Twilio</strong> — SMS delivery and
            inbound webhook routing for delegated tasks.
          </li>
          <li>
            <strong className="text-white/85">MongoDB Atlas (managed)</strong> — durable
            storage for the owner's conversations, reminders, memories, and contacts.
          </li>
          <li>
            <strong className="text-white/85">Web Push (VAPID)</strong> — browser-native
            push notifications for reminders.
          </li>
        </ul>
      </Section>

      <Section id="retention" title="Data retention">
        <p>
          Owner-created content (chats, memories, reminders, contacts, tasks) is
          retained until the owner deletes it from the app. SMS message logs are
          retained for the lifetime of the related task and may be purged at any time
          by request. Backup snapshots may persist for a short rolling window for
          disaster-recovery purposes.
        </p>
      </Section>

      <Section id="security" title="Security">
        <p>
          Data in transit is encrypted via TLS. Stored data resides on managed
          infrastructure with industry-standard access controls. API credentials are
          held in environment configuration and are never exposed to the client.
        </p>
        <p>
          No system is perfectly secure. If you suspect a vulnerability or
          unauthorized access, please contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </Section>

      <Section id="rights" title="Your rights">
        <p>
          You may request access to, correction of, or deletion of any personal data
          Journey holds about you. To exercise these rights — including opting out of
          SMS or requesting full deletion of a contact record — email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>
          . Requests are honored within a reasonable timeframe, typically under 30 days.
        </p>
      </Section>

      <Section id="children" title="Children">
        <p>
          Journey is not directed at children under 13 and does not knowingly collect
          personal information from them. If you believe a child's information has
          been collected, contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>{" "}
          and it will be deleted.
        </p>
      </Section>

      <Section id="changes" title="Changes to this policy">
        <p>
          Material changes will be reflected here with an updated "last updated" date.
          Continued use of the service after a change constitutes acceptance of the
          revised policy.
        </p>
      </Section>

      <Section id="contact" title="Contact">
        <p>
          For any privacy-related question or request:{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-cyan-300 hover:text-cyan-200">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      </Section>
    </div>
  );
}
